import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cmsgov/ds-healthcare-gov';
import IncomeFields from '../IncomeFields';
import { formatCurrency } from '../../../utilities/currency';
import { getIncomeTypeIndex } from '../../../utilities/incomeCalculations';
import { sendEvent } from '../../../utilities/analytics';
import IncomeRow from '../IncomeRow';

import type { Income } from '../IncomeCalculator';
import type { OrderedMap } from 'immutable';

type Props = {
  incomes: OrderedMap<string, Income>;
  incomeTotal: number;
  onAddNewIncome: (incomeState: Income) => void;
  handleUpdateIncomeEntry: (incomeObject: Income, id: string) => void;
  handleRemoveIncomeEntry: (id: string) => void;
};

const IncomeContainer = (props: Props) => {
  const [currentlyAdding, setCurrentlyAdding] = useState<boolean>(false);
  const { t } = useTranslation();

  const handleAddNewIncome = (incomeState: Income) => {
    sendEvent('estimate income', 'calculated income', 'income', 'calculate_income_unauthenticated');
    props.onAddNewIncome(incomeState);
    setCurrentlyAdding(false);
  };

  const cachedElement = useRef<HTMLElement | null>(null);
  const firstUpdate = useRef(true);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    if (cachedElement.current) {
      cachedElement.current.focus();
    }
  }, [props.incomes, props.incomeTotal, currentlyAdding]);

  return (
    <div className="ds-l-row ds-u-padding-y--3 ds-u-border-top--2 ds-u-border--dark">
      <div className="ds-l-col--12">
        <h2 className="ds-text-heading--2xl ds-u-justify-content--between ds-u-display--flex">
          <span>{t('income')}</span>
          <span>{formatCurrency(props.incomeTotal)}</span>
        </h2>
        <p>{t('enterMultipleIncomeText')}</p>
      </div>

      {props.incomes
        .map((income, key) => {
          const incomeTypeIndex = getIncomeTypeIndex(income, props.incomes);
          return (
            <IncomeRow
              key={income.id}
              id={key}
              editing={false}
              incomeType={income.incomeType}
              incomeTypeIndex={incomeTypeIndex! + 1}
              amount={income.amount}
              startDate={income.startDate}
              endDate={income.endDate}
              incomeFrequency={income.incomeFrequency}
              calculatedTotal={income.calculatedTotal}
              onUpdateIncomeEntry={props.handleUpdateIncomeEntry}
              onRemoveIncome={props.handleRemoveIncomeEntry}
            />
          );
        })
        .valueSeq()}

      {currentlyAdding ? (
        <IncomeFields
          onCancelAddIncome={() => {
            setCurrentlyAdding(false);
          }}
          onDoneClick={handleAddNewIncome}
        />
      ) : (
        <div className="ds-l-col--12">
          <Button
            className="ic-add-income ds-u-margin-top--3"
            inputRef={(ele) => {
              cachedElement.current = ele;
            }}
            onClick={() => setCurrentlyAdding(true)}
          >
            {t('addIncomeBtn')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default IncomeContainer;
